import { useEffect } from "react";
import { Phone, Globe } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Marquee } from "@/components/Marquee";
import { Reveal } from "@/components/Reveal";
import { WhatsAppIcon } from "@/components/WhatsAppIcon";
import { CONTACTS, WEBSITE, WHATSAPP_URL, COMPANY, FLYER_SERVICES } from "@/data/site";

export default function ContactPage() {
  useEffect(() => {
    document.title = "Contact SMS Electrical & Fiber Optic Installers | 242-809-6853";
    const link = document.querySelector("link[rel='canonical']");
    if (link) link.href = "https://smselectrical.com/contact";
  }, []);

  return (
    <div data-testid="contact-page">
      <Navbar />
      <main>
        <section className="relative flex min-h-[52vh] items-end overflow-hidden" data-testid="contact-hero">
          <img
            src="/assets/contact-header.jpg"
            alt="SMS Electrical technician working on an electrical panel"
            className="absolute inset-0 h-full w-full object-cover"
            data-testid="contact-hero-image"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F19] via-[#0B0F19]/55 to-[#0B0F19]/20" />
          <div className="relative mx-auto w-full max-w-7xl px-4 pb-14 pt-32 sm:px-6 lg:px-8">
            <Reveal>
              <p className="font-mono-tech text-xs font-semibold uppercase tracking-[0.35em] text-[#FF5B00]">Contact</p>
              <h1 className="mt-4 font-display text-4xl font-bold uppercase leading-tight tracking-tight text-white sm:text-5xl lg:text-6xl" data-testid="contact-heading">
                Let&apos;s Talk About <span className="text-[#FF5B00]">Your Project</span>
              </h1>
              <p className="mt-4 font-mono-tech text-xs uppercase tracking-[0.25em] text-[#F8FAFC]" data-testid="contact-company">
                {COMPANY}
              </p>
            </Reveal>
          </div>
        </section>

        <Marquee items={FLYER_SERVICES} testId="contact-services-marquee" />

        <section className="bg-[#0B0F19]" data-testid="contact-details">
          <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
            <Reveal>
              <h2 className="font-display text-2xl font-bold uppercase tracking-tight text-[#F8FAFC] sm:text-3xl" data-testid="contact-reach-heading">
                Reach SMS <span className="text-[#FF5B00]">Directly</span>
              </h2>
              <div className="mt-4 h-1 w-16 bg-[#FF5B00]" />
            </Reveal>

            <div className="mt-10 grid gap-px border border-[#1E293B] bg-[#1E293B] sm:grid-cols-2 lg:grid-cols-4">
              {CONTACTS.map((c, i) => (
                <Reveal key={c.label} delay={i * 0.08}>
                  <div className="flex h-full flex-col bg-[#111726] p-7" data-testid={`contact-phone-${i}`}>
                    <Phone className="h-6 w-6 text-[#FF5B00]" strokeWidth={1.8} />
                    <span className="mt-5 font-mono-tech text-[10px] uppercase tracking-[0.25em] text-[#64748B]">
                      {c.whatsapp ? "Phone & WhatsApp" : "Phone"}
                    </span>
                    <a
                      href={c.tel}
                      data-testid={`contact-tel-${i}`}
                      className="mt-2 font-display text-2xl font-bold tracking-wide text-[#F8FAFC] transition-colors duration-300 hover:text-[#FF5B00]"
                    >
                      {c.label}
                    </a>
                    {c.whatsapp && (
                      <a
                        href={WHATSAPP_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="contact-whatsapp-button"
                        className="mt-4 flex w-fit items-center gap-2 border border-[#25D366] px-4 py-2 font-mono-tech text-[11px] font-semibold uppercase tracking-[0.15em] text-[#25D366] transition-colors duration-300 hover:bg-[#25D366] hover:text-[#0B0F19]"
                      >
                        <WhatsAppIcon className="h-4 w-4" />
                        WhatsApp
                      </a>
                    )}
                  </div>
                </Reveal>
              ))}
              <Reveal delay={0.24}>
                <a
                  href={WEBSITE.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex h-full flex-col bg-[#111726] p-7 transition-colors duration-300 hover:bg-[#161E31]"
                  data-testid="contact-website"
                >
                  <Globe className="h-6 w-6 text-[#FF5B00]" strokeWidth={1.8} />
                  <span className="mt-5 font-mono-tech text-[10px] uppercase tracking-[0.25em] text-[#64748B]">Website</span>
                  <span className="mt-2 font-display text-2xl font-bold tracking-wide text-[#F8FAFC] transition-colors duration-300 group-hover:text-[#FF5B00]">
                    {WEBSITE.label}
                  </span>
                </a>
              </Reveal>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
